import React, { memo } from 'react';
import messages from '../Messages';
import { IOption } from 'components/Select/Select';
import Select from 'components/Select';
import { BatchAddItem } from '../ProjectMembers';
import { IUser, IUsers } from 'store/users/usersType';
import { ICompanyMember, ICompanyRole } from 'store/companies/companiesTypes';
import i18n from '../../../../i18n';

type ComponentProps = {
  users: IUsers['map'];
  roles: { [key: number]: ICompanyRole };
  companyMembers: { [key: number]: ICompanyMember };
  onSelectUser: (usersOptions: IOption[]) => void;
  onBatchAdd: (users: BatchAddItem[]) => void;
};

const ProjectMembersSelect: React.FC<ComponentProps> = ({
  users,
  roles,
  companyMembers,
  onSelectUser,
  onBatchAdd,
}) => {
  const companyUsers = Object.values(users)
    .filter((user: IUser) =>
      Object.values(companyMembers).find(
        (v) => v.userId === user.id && !v!.inactive
      )
    )
    .reduce((usersList: IOption[], user: IUser) => {
      usersList.push({
        label: user.name,
        value: user.id,
      });
      return usersList;
    }, []);

  const getPasteInfoFromTable = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const htmlTable = e.clipboardData.getData('text/html');
    const isTableExists = htmlTable.search(/<table/gm);
    if (isTableExists !== -1) {
      e.preventDefault();
      const parsed = new DOMParser().parseFromString(htmlTable, 'text/html');
      let filteredRows = Array.from(parsed.getElementsByTagName('tr'));

      /*
            detecting header row by "last name" column title
            if its title change, update here
          */
      const hasHeader = filteredRows[0].innerText.includes(
        messages.headerLastNameColumn
      );
      // delete header row
      if (hasHeader) {
        filteredRows.shift();
      }

      // filter empty rows
      filteredRows = filteredRows.filter((r) => r.innerText !== '');

      let cells: string[][] = [];
      cells = (filteredRows as any).map((row: any) => {
        return Array.from(
          new Set(row.getElementsByTagName('td')),
          (td: any) => td.innerText
        );
      });

      // remove duplicates rows
      cells = Array.from(
        new Set(cells.map(JSON.stringify as any)) as any,
        JSON.parse as any
      );

      const found: BatchAddItem[] = [];
      cells.forEach((cell) => {
        const name = cell[0].trim();
        const roleName = cell[1].trim();
        const foundUser = companyUsers.find((cu: IOption) => cu.label === name);
        const user = foundUser ? users[Number(foundUser.value)] : undefined;
        let relatedRole: ICompanyRole | undefined;
        Object.values(roles).forEach((role) => {
          if (role!.name === roleName && !role?.isProjectRole)
            relatedRole = role;
        });
        const title =
          cell.length > 1 ? cell[2] : cell.length > 1 ? cell[1] : '';
        let projectRole: ICompanyRole | undefined;
        Object.values(roles).forEach((role) => {
          if (role!.name === title && role?.isProjectRole) projectRole = role;
        });
        found.push({
          user: user,
          name: name,
          title: title.trim(),
          relatedRole,
          relatedRoleTitle: roleName,
          projectRole,
          addRole: relatedRole?.users.length === 1,
        });
      });

      onBatchAdd(found);
    }
  };

  return (
    <Select
      onChangeValues={onSelectUser}
      values={[]}
      label={
        i18n.t('addMemberToProject', { ns: 'companyManagement' }) as string
      }
      options={companyUsers}
      placeholder={
        i18n.t('addMemberToProject', { ns: 'companyManagement' }) as string
      }
      onPaste={getPasteInfoFromTable}
    />
  );
};

export default memo(ProjectMembersSelect);
